import React from 'react';
import { Link } from 'react-router-dom';

/**
 * The printing surface (05 §1.3, §2.2; concept doc-3), generalized from
 * `ClassDetail.js:311-330` and `:387-402`.
 *
 * It is a PLATFORM section, not a per-Family render style: which books printed a
 * thing is the model's own structure, so every printed Family gets the same
 * three pieces and none of them is authored.
 *
 *   `PrintingsPicker`   one button per printing, inside the stat block, the
 *                       resolved one active
 *   `AlsoAppearsIn`     the other printings, as links to their books
 *   `IncompleteNotice`  the completeness marker — the resolved printing is a
 *                       MENTION rather than a write-up
 *
 * 02-parity-inventory row 6 records the picker as a parity GAIN: the shipped
 * class page is the only one of the ten details that had one, while spells,
 * feats and items already returned a `printings[]` nobody rendered. Giving all
 * of them the picker is the point of doing the printings machinery once.
 *
 * THE PIN TRAVELS THROUGH THE URL, and that is the one deliberate behavior
 * change here. `ClassDetail` held the selected variant in component state, so
 * the choice was invisible to the address bar, unshareable, and lost on reload —
 * while `?rulebook=` already existed as the pin on three Families (02b Part 2)
 * and did exactly the same job from the server side. Under MRCS the printing's
 * values are resolved server-side (04 §5.1), so a local selection could not work
 * at all without a second request; routing the button through the URL makes the
 * picker and the deep link ONE mechanism instead of two that disagree.
 */

/** The printing a payload resolved to: the pinned one, else the canonical one. */
export function resolvedPrinting(printings, pin) {
  const rows = Array.isArray(printings) ? printings : [];
  if (!rows.length) return null;
  return (pin && rows.find((p) => p.rulebook_slug === pin))
    || rows.find((p) => p.is_canonical)
    || rows[0];
}

/**
 * The picker.
 *
 * Rendered only for content printed more than once, which is the shipped rule —
 * `characterClass.variants?.length > 1` — and the right one: a button that is
 * the only button is not a choice.
 */
export function PrintingsPicker({ printings, resolved, onPin }) {
  const rows = Array.isArray(printings) ? printings : [];
  if (rows.length < 2) return null;
  return (
    <>
      {/* Pick chrome: the pinned printing's book still prints via the
          rulebook row, so the picker itself stays off paper (#194). */}
      <div className="stat-block-section print:hidden">Variants</div>
      <div className="flex flex-wrap gap-2 mb-2 print:hidden">
        {rows.map((printing) => (
          <button
            key={printing.rulebook_slug}
            type="button"
            onClick={() => onPin(printing.rulebook_slug)}
            className={`alphabetical-button ${
              resolved && resolved.rulebook_slug === printing.rulebook_slug
                ? 'alphabetical-button-active'
                : 'alphabetical-button-inactive'
            }`}
          >
            {printing.rulebook_name}
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * A book's name as a link to its page under /rulebooks: the one place a
 * detail names its source (issue 1238), here and in "Also Appears In". With
 * no slug it is the plain name.
 */
export function RulebookLink({ slug, name, from, listFrom, className = 'class-level-link underline' }) {
  if (!slug) return <>{name}</>;
  return (
    <Link
      to={`/rulebooks?slug=${encodeURIComponent(slug)}`}
      state={{ from, listFrom }}
      className={className}
    >
      {name}
    </Link>
  );
}

/** The other books this entry appears in, as links. */
export function AlsoAppearsIn({ printings, resolved, from, listFrom }) {
  const rows = (Array.isArray(printings) ? printings : [])
    .filter((p) => !resolved || p.rulebook_slug !== resolved.rulebook_slug);
  if (!rows.length || (printings || []).length < 2) return null;
  return (
    <div className="card print:hidden">
      <h2 className="section-header">Also Appears In</h2>
      <ol className="list-decimal list-inside space-y-1 text-secondary">
        {rows.map((printing) => (
          <li key={printing.rulebook_slug}>
            <RulebookLink slug={printing.rulebook_slug} name={printing.rulebook_name} from={from} listFrom={listFrom} />
            {/* The mention label of concept doc-3: this book REFERS to the
                entry, it does not print it. Production had no way to say so —
                its variant list could not tell a write-up from a cross
                reference — so the label is new wherever a mention exists. */}
            {printing.completeness === 'mention' && (
              <span className="text-muted">
                {' '}(referenced{printing.page ? `, p. ${printing.page}` : ''})
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * THE COMPLETENESS MARKER (concept doc-3).
 *
 * `printing.completeness` is the converter's recorded answer to "does this book
 * actually print the thing, or only mention it", and this is the one place a
 * reader sees it. The shipped class page asked the same question by probing
 * three specific columns (`!class_features_html && !class_features &&
 * !advancement_html`, `ClassDetail.js:170`) — a hardcoded list of the keys that
 * happen to carry a class write-up, which no other Family could reuse. The flag
 * says it once, for every Family, and the copy is the shipped copy.
 */
export function IncompleteNotice({ resolved, labelPlural = 'entry' }) {
  if (!resolved || resolved.completeness !== 'mention') return null;
  return (
    <div className="card" style={{ borderColor: 'var(--accent)', background: 'rgba(124,29,38,0.05)' }}>
      <h2 className="section-header">Work in Progress</h2>
      <div className="text-secondary space-y-2">
        <p>
          This {labelPlural} is not yet complete. Reach out to{' '}
          <a href="mailto:support@thedragonstable.com" className="class-level-link underline">
            support@thedragonstable.com
          </a>{' '}
          to request it or to contribute.
        </p>
      </div>
    </div>
  );
}
